import { Check, X, Trophy, Heart, Clock, Dumbbell, Gavel } from "lucide-react";
import { Card } from "@/components/ui/card";
import {
    Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const allowed = [
    "Fitness & health mobile apps (iOS & Android)",
    "Workout trackers, macro loggers & fitness tools",
    "Digital fitness programs & online coaching",
    "Real App Store, Play Store, or website URLs",
    "A public X post from your handle",
    "An optional specialty tag & short honest pitch",
];
const forbidden = [
    "Anything that isn't health & fitness",
    "Chat / invite links (Discord, Telegram, WhatsApp, etc.)",
    "Link shorteners (bit.ly, t.co, tinyurl, ...)",
    "Tracking parameters (utm_*, fbclid, gclid, ...)",
    "NSFW / adult content or malicious links",
];

export default function Rules() {
    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
            <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Rules &amp; how ranking works</h1>
            <p className="text-muted-foreground mt-2">
                Crowd rank is likes on your listing tweet. Paid featured slots are separate and labelled when they open.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mt-8">
                <Card className="bg-card border-border p-5" data-testid="rules-allowed">
                    <h2 className="font-heading font-semibold flex items-center gap-2 mb-3"><Check className="h-5 w-5 text-brand" /> Allowed</h2>
                    <ul className="space-y-2.5">
                        {allowed.map((a) => (
                            <li key={a} className="flex gap-2 text-sm"><Check className="h-4 w-4 text-brand shrink-0 mt-0.5" /> {a}</li>
                        ))}
                    </ul>
                </Card>
                <Card className="bg-card border-border p-5" data-testid="rules-forbidden">
                    <h2 className="font-heading font-semibold flex items-center gap-2 mb-3"><X className="h-5 w-5 text-heat" /> Forbidden</h2>
                    <ul className="space-y-2.5">
                        {forbidden.map((f) => (
                            <li key={f} className="flex gap-2 text-sm"><X className="h-4 w-4 text-heat shrink-0 mt-0.5" /> {f}</li>
                        ))}
                    </ul>
                </Card>
            </div>

            <div className="grid sm:grid-cols-3 gap-4 mt-4">
                <Card className="bg-card border-border p-5">
                    <Heart className="h-6 w-6 text-brand mb-2" />
                    <h3 className="font-heading font-semibold">Most likes wins Crowd #1</h3>
                    <p className="text-sm text-muted-foreground mt-1">Rank is likes on one listing tweet. Boost is a like on that tweet.</p>
                </Card>
                <Card className="bg-card border-border p-5">
                    <Clock className="h-6 w-6 text-brand mb-2" />
                    <h3 className="font-heading font-semibold">Ties go to the early bird</h3>
                    <p className="text-sm text-muted-foreground mt-1">Equal likes? Whoever listed first stays higher.</p>
                </Card>
                <Card className="bg-card border-border p-5">
                    <Trophy className="h-6 w-6 text-brand mb-2" />
                    <h3 className="font-heading font-semibold">Featured is paid and labelled</h3>
                    <p className="text-sm text-muted-foreground mt-1">When featured slots open, they sit above the crowd board as paid placement. They do not replace likes.</p>
                </Card>
            </div>

            <Card className="bg-card border-border p-5 mt-4" data-testid="rules-market">
                <h2 className="font-heading font-semibold flex items-center gap-2 mb-3">
                    <Gavel className="h-5 w-5 text-brand" /> Sponsor market rules
                </h2>
                <p className="text-sm text-muted-foreground mb-4">
                    The marketplace is a separate product from the crowd board. Creators list real
                    inventory with metrics attached; sponsors bid from a wallet on a concrete
                    deliverable.
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <h3 className="text-sm font-semibold mb-2">Money</h3>
                        <ul className="space-y-2 text-sm">
                            <li className="flex gap-2"><Check className="h-4 w-4 text-brand shrink-0 mt-0.5" /> A bid is a hold, not a charge. Funds leave "available" into "held".</li>
                            <li className="flex gap-2"><Check className="h-4 w-4 text-brand shrink-0 mt-0.5" /> Outbid → your hold releases back to the wallet instantly.</li>
                            <li className="flex gap-2"><Check className="h-4 w-4 text-brand shrink-0 mt-0.5" /> Auction close captures the winner only. Everyone else's hold is released.</li>
                            <li className="flex gap-2"><Check className="h-4 w-4 text-brand shrink-0 mt-0.5" /> Platform fee is 12%, taken from the creator's payout when the sponsor accepts proof.</li>
                            <li className="flex gap-2"><Check className="h-4 w-4 text-brand shrink-0 mt-0.5" /> No proof within 7 days of close → the winner's full amount refunds to their wallet automatically.</li>
                        </ul>
                    </div>
                    <div>
                        <h3 className="text-sm font-semibold mb-2">Listings &amp; metrics</h3>
                        <ul className="space-y-2 text-sm">
                            <li className="flex gap-2"><Check className="h-4 w-4 text-brand shrink-0 mt-0.5" /> Every card shows: creator, niche, avg views, slot type, current bid, time left.</li>
                            <li className="flex gap-2"><Check className="h-4 w-4 text-brand shrink-0 mt-0.5" /> Required metrics before a slot can publish: handle, platforms, followers, avg views (30d), niche, next event date.</li>
                            <li className="flex gap-2"><Check className="h-4 w-4 text-brand shrink-0 mt-0.5" /> Metrics are labelled <strong>claimed</strong> until verified. Watch time and results are optional.</li>
                            <li className="flex gap-2"><Check className="h-4 w-4 text-brand shrink-0 mt-0.5" /> Proof of delivery is required per listing; the sponsor reviews within 48 hours.</li>
                            <li className="flex gap-2"><Check className="h-4 w-4 text-brand shrink-0 mt-0.5" /> Feedback build: wallets use demo credits and seeded sponsor budgets. No card is charged.</li>
                        </ul>
                    </div>
                </div>
            </Card>

            <Card className="bg-card border-border p-5 mt-4">
                <h2 className="font-heading font-semibold flex items-center gap-2 mb-2"><Dumbbell className="h-5 w-5 text-brand" /> Ranking mechanics FAQ</h2>
                <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="1">
                        <AccordionTrigger data-testid="faq-1">What does it cost to list?</AccordionTrigger>
                        <AccordionContent>The crowd board is free. You post one tweet that names your app and outbid.fit, then paste the URL.</AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="2">
                        <AccordionTrigger data-testid="faq-2">How do I move up?</AccordionTrigger>
                        <AccordionContent>Ask people to like the listing tweet. The Boost button opens that like. Likes can take a few minutes to refresh.</AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="3">
                        <AccordionTrigger data-testid="faq-3">Can I change the tweet later?</AccordionTrigger>
                        <AccordionContent>No. One tweet per listing. One active listing per app. That stops tweet swapping.</AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="4">
                        <AccordionTrigger data-testid="faq-4">Do I need an account?</AccordionTrigger>
                        <AccordionContent>No. Listing is account-free. You can optionally add an email for like-pass alerts and featured-slot news.</AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="5">
                        <AccordionTrigger data-testid="faq-5">What are featured slots?</AccordionTrigger>
                        <AccordionContent>Paid placement above the crowd board, labelled as paid. Crowd #1 stays likes. Featured is not open until the board has heat.</AccordionContent>
                    </AccordionItem>
                </Accordion>
            </Card>
        </div>
    );
}
